import { checkCookieExists, getCookieValue, setCookie } from '@utils/web-storage';
import { recordMarketingSiteLD } from '@utils/launch-darkly';
import { userAgentIsBot } from '@utils/bot-detection';

const COOKIE_KEY = 'fundrise__mst';
const COOKIE_MAX_AGE = '2592000'; // 30 days in seconds

/**
 * Client-side A/B test bucketing for *unauthenticated* pages only.
 *
 * Bypasses LaunchDarkly for performance -- randomly assigns a variant, persists it in a cookie,
 * and records the assignment via a dedicated analytics endpoint.
 *
 * @example A/B test (defaults to ['control', 'test'] variants)
 * getMarketingSiteTestVariant('2000-01-01-some-flag-name')
 *
 * @example A/B/C test
 * getMarketingSiteTestVariant('2000-01-01-some-flag-name', ['control', 'test-behavior-b', 'test-behavior-c'])
 *
 * @note The API allowlists supported flags here:
 *   https://github.com/Fundrise/api-services/blob/e1ef4b57/api-services/src/main/java/com/fundrise/api/analytics/AbTestService.java#L130
 * @note A real LD flag does not need to be created.
 * @note Generic flag support is tracked in https://fundrise.atlassian.net/browse/API-633
 */
export const getMarketingSiteTestVariant = (
	flag: string,
	variants: [string, ...string[]] = ['control', 'test']
): string => {
	if (userAgentIsBot()) return variants[0];

	try {
		const testConfig = JSON.parse(checkCookieExists(COOKIE_KEY) ? (getCookieValue(COOKIE_KEY) as string) : '{}');
		if (!Object.hasOwn(testConfig, flag) || !variants.includes(testConfig[flag])) {
			testConfig[flag] = variants[Math.floor(Math.random() * variants.length)];

			// timeout to avoid delays of setting cookie and logging analytics by doing them in the background
			setTimeout(async () => {
				setCookie(COOKIE_KEY, JSON.stringify(testConfig), COOKIE_MAX_AGE);
				await recordMarketingSiteLD(flag, testConfig[flag]);
			});
		}
		return testConfig[flag] as string;
	} catch {
		return variants[0]; // exclude from test enrollment and behavior if cookies are unavailable
	}
};
